import React, {
  useCallback,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from 'react';
import UploadFilesControl from 'chameleon/ui-stack/ui/forms/upload-files-control';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import Input from 'chameleon/ui-stack/ui/base/input';
import {
  issueTypes,
  schoolOrInstitutional,
  schoolOrInstitutionalEnabledIssueTypes,
} from './constants';

const ContactUsPreapForm = forwardRef((props, ref) => {
  const { onFormChange } = props;
  const emptySelector = {};
  const labelClassName =
    'text-14 font-medium text-black block mb-2 whitespace-nowrap';

  const inputClassName =
    'px-2 py-4 w-full border border-solid border-gray-400 rounded-0.6 shadow-lg';

  const textareaClassName = `${inputClassName} h-84 resize-none`;

  const defaultValues = {
    description: '',
    subject: '',
    isInstitutionalDevice: 'yes',
    issueType: undefined,
  };

  const [selectedIssueType, setSelectedIssueType] = useState(emptySelector);
  const [inputValues, setInputValues] = useState(defaultValues);
  const [selectedSchoolOrInstitutional, setSelectedSchoolOrInstitutional] =
    useState(emptySelector);

  const descriptionRef = useRef(null);
  const uploadFilesRef = useRef(null);

  const isSchoolOrInstitutionalEnabled =
    schoolOrInstitutionalEnabledIssueTypes.includes(selectedIssueType.id);

  const onTypeOfIssueChange = useCallback((selection) => {
    setInputValues((currentValues) => ({
      ...currentValues,
      issueType: selection.label,
      isInstitutionalDevice: isSchoolOrInstitutionalEnabled
        ? currentValues.isInstitutionalDevice
        : defaultValues.isInstitutionalDevice,
    }));
    // reset to default dropdown
    !isSchoolOrInstitutionalEnabled &&
      setSelectedSchoolOrInstitutional(emptySelector);
    setSelectedIssueType(selection);
  }, []);

  const onSchoolOrInstitutionalChange = useCallback(
    (selection) => {
      setInputValues((currentValues) => ({
        ...currentValues,
        isInstitutionalDevice: selection.label,
      }));
      setSelectedSchoolOrInstitutional(selection);
    },
    [issueTypes],
  );

  useImperativeHandle(ref, () => ({
    getUploadedFiles: () => uploadFilesRef.current.getUploadedFiles(),
    clearForm: () => {
      setSelectedIssueType(emptySelector);
      setSelectedSchoolOrInstitutional(emptySelector);
      if (descriptionRef.current) {
        descriptionRef.current.value = '';
      }
      uploadFilesRef.current.clearFiles();
      setInputValues(defaultValues);
    },
  }));

  const onDescriptionChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((currentValues) => ({
      ...currentValues,
      description: value,
    }));
  }, []);

  const onSubjectChange = useCallback((event) => {
    const { value } = event.target;
    setInputValues((currentValues) => ({
      ...currentValues,
      subject: value,
    }));
  }, []);

  useEffect(() => {
    const values = {
      ...inputValues,
    };
    onFormChange(values, {
      selectedIssueType,
    });
  }, [inputValues, selectedIssueType]);

  return (
    <>
      <div className="text-13 col-span-3">Required fields *</div>
      <div>
        <label className={labelClassName}>Type of issue *</label>
        <AutocompleteSelect
          placeholder={{ label: 'Select a Type' }}
          portalContainerClassName="z-overModals"
          className="w-full"
          options={issueTypes}
          onChange={onTypeOfIssueChange}
          value={selectedIssueType}
        />
      </div>
      <div>
        <label className={labelClassName}>
          Is this a school/institutional device?
        </label>
        <AutocompleteSelect
          disableDropdown={!isSchoolOrInstitutionalEnabled}
          placeholder={{ label: 'Select...' }}
          portalContainerClassName="z-overModals"
          className="w-full"
          options={schoolOrInstitutional}
          onChange={onSchoolOrInstitutionalChange}
          value={selectedSchoolOrInstitutional}
        />
      </div>
      <div className="col-span-3">
        <label htmlFor="description" className={labelClassName}>
          Subject *
        </label>
        <Input onChange={onSubjectChange} value={inputValues.subject} />
      </div>
      <div className="col-span-3">
        <label htmlFor="description" className={labelClassName}>
          Description *
        </label>
        <div className="text-gray-700 text-14 mb-2">
          Describe the experience you are currently having and, if possible, the
          experience you would expect. Include as much information as possible.
          This will help us resolve the issue.
        </div>
        <textarea
          id="description"
          onChange={onDescriptionChange}
          ref={descriptionRef}
          placeholder=""
          className={textareaClassName}
          defaultValue=""
        />
      </div>

      <div className="col-span-3">
        <UploadFilesControl
          ref={uploadFilesRef}
          label="Share Screenshots"
          labelClassName="text-black block mb-2"
        />
      </div>
    </>
  );
});

export default ContactUsPreapForm;
